'use client'

import Image from 'next/image'
import { useSyncExternalStore } from 'react'
import { imageFocal } from '@/lib/contenuEditable'

/**
 * Hero principale Club Démo — refonte 2026-06-04 (style TheCastleClub) :
 *
 *   1. Vidéo de fond plein écran — multi-résolution (desktop 1080p / mobile 720p)
 *      H.264, autoplay/muted/loop, poster fallback
 *   2. Logo sceau LDC ancré en HAUT centré
 *   3. Tagline 2 lignes serif italique centrée dans l'espace logo→bouton
 *   4. Trait or + bouton "Rejoindre le Club" ancré en BAS
 *
 * Stratégie pro pour préserver qualité + fluidité :
 *   - `<source media>` : le browser télécharge SEULEMENT la résolution
 *     adaptée à son viewport (mobile = 6 MB, desktop = 35 MB)
 *   - `preload="metadata"` : juste les ~2 KB de header, pas la vidéo
 *   - `prefers-reduced-motion` : poster statique au lieu de vidéo
 *   - `Save-Data` / connexion lente (2G) : poster statique
 *
 * Layout vertical flex en 3 zones (top/middle flex-grow/bottom) — la
 * tagline est ainsi centrée DANS l'espace restant entre logo et bouton,
 * pas dans le viewport entier (évite tout chevauchement).
 *
 * Hauteur 100dvh > 100svh > 100vh — gère la rétractation barre URL mobile
 * sans saut.
 */

/* useSyncExternalStore = pattern React 19 SSR-safe pour s'abonner à
   matchMedia sans setState dans useEffect (évite les cascading renders). */
function useReducedMotion(): boolean {
  return useSyncExternalStore(
    (callback) => {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
      mq.addEventListener('change', callback)
      return () => mq.removeEventListener('change', callback)
    },
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    () => false,
  )
}

/* Détection Save-Data + connexion 2G via Network Information API.
   Non supporté par Safari → toujours `false` (pas pénalisant). */
type NetworkInformation = {
  saveData?: boolean
  effectiveType?: string
  addEventListener?: (type: 'change', cb: () => void) => void
  removeEventListener?: (type: 'change', cb: () => void) => void
}
type NavigatorWithConnection = Navigator & { connection?: NetworkInformation }

function useShouldSkipVideo(): boolean {
  return useSyncExternalStore(
    (callback) => {
      const conn = (navigator as NavigatorWithConnection).connection
      if (!conn?.addEventListener) return () => {}
      conn.addEventListener('change', callback)
      return () => conn.removeEventListener?.('change', callback)
    },
    () => {
      const conn = (navigator as NavigatorWithConnection).connection
      if (!conn) return false
      if (conn.saveData) return true
      if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') return true
      return false
    },
    () => false,
  )
}

/**
 * Contenu éditable depuis le CRM (section `hero` de la page d'accueil).
 *
 * Tout est optionnel : chaque valeur absente OU vide retombe sur celle
 * d'origine ci-dessous. C'est délibéré — la bannière est le premier écran du
 * site, elle ne doit jamais se retrouver nue parce qu'un champ a été effacé
 * par mégarde dans l'éditeur, ni parce que le CRM est injoignable.
 *
 * La mise en forme (interlettrage, `clamp()`, dégradé, filet) reste ici et
 * n'est pas éditable : c'est elle qui fait l'identité de la bannière, et
 * l'exposer reviendrait à laisser casser la page sans le vouloir.
 */
export interface HomeHeroContent {
  title?: string
  subtitle?: string
  logoImage?: string
  /** Masque le logo permanent posé sur l'image du hero (la navbar garde le sien). */
  hideLogo?: boolean
  backgroundVideo?: string
  backgroundVideoMobile?: string
  backgroundImage?: string | { src: string; pos: string }
  primaryButton?: { label?: string; href?: string }
  /** Hero adaptatif à l'événement : date affichée sous le titre. */
  date?: string
  /** Mention (ex. « Billetterie bientôt ouverte ») quand il n'y a pas de bouton. */
  mention?: string
}

const DEFAUTS = {
  title: 'Rassembler par la culture',
  subtitle: 'Inspirer par la rencontre',
  logoImage: '/demo/placeholder.svg',
  backgroundVideo:
    '',
  backgroundVideoMobile:
    '',
  backgroundImage: '/demo/placeholder.svg',
  ctaLabel: 'Rejoindre le Club',
  ctaHref: '/devenir-membre',
} as const

/** Une chaîne vide vaut « non renseigné » : on garde alors la valeur d'origine. */
function ou(valeur: string | undefined, defaut: string): string {
  return typeof valeur === 'string' && valeur.trim() !== '' ? valeur : defaut
}

export default function HomeHero({ content }: { content?: HomeHeroContent }) {
  const reducedMotion = useReducedMotion()
  const skipVideoForData = useShouldSkipVideo()
  // Pas de source vidéo → on affiche l'IMAGE de fond (sinon <video> vide = fond noir).
  const hasVideoSrc = Boolean(
    (content?.backgroundVideo && content.backgroundVideo.trim() !== '') ||
    (content?.backgroundVideoMobile && content.backgroundVideoMobile.trim() !== '')
  )
  const showVideo = !reducedMotion && !skipVideoForData && hasVideoSrc

  const titre = ou(content?.title, DEFAUTS.title)
  const sousTitre = ou(content?.subtitle, DEFAUTS.subtitle)
  const logo = ou(content?.logoImage, DEFAUTS.logoImage)
  const hideLogo = content?.hideLogo === true
  const videoDesktop = ou(content?.backgroundVideo, DEFAUTS.backgroundVideo)
  const videoMobile = ou(content?.backgroundVideoMobile, DEFAUTS.backgroundVideoMobile)
  const bg = imageFocal(content?.backgroundImage)
  const poster = bg.src || DEFAUTS.backgroundImage
  // Le cadrage éditorial ne s'applique qu'à une image réellement fournie par
  // le CRM — le placeholder d'origine garde son cadrage codé en dur.
  const posterObjectPosition = bg.src ? bg.pos : 'center 44%'
  const ctaLabel = ou(content?.primaryButton?.label, DEFAUTS.ctaLabel)
  const ctaHref = ou(content?.primaryButton?.href, DEFAUTS.ctaHref)
  // DA Carlton — hero « titre seul » : on masque sous-titre et CTA quand le
  // champ est explicitement vide (au lieu de retomber sur un défaut).
  const montrerSousTitre = typeof content?.subtitle === 'string' && content.subtitle.trim() !== ''
  const montrerCta = typeof content?.primaryButton?.label === 'string' && content.primaryButton.label.trim() !== ''
  const dateAffichee = typeof content?.date === 'string' && content.date.trim() !== '' ? content.date : ''
  const mention = typeof content?.mention === 'string' && content.mention.trim() !== '' ? content.mention : ''

  return (
    <section
      className="demo-hero-section relative w-full overflow-hidden bg-brand-ink"
      style={{ minHeight: '100vh' }}
    >
      <style>{`
        @supports (height: 100svh) {
          .demo-hero-section, .demo-hero-stack { min-height: 100svh !important; }
        }
        @supports (height: 100dvh) {
          .demo-hero-section, .demo-hero-stack { min-height: 100dvh !important; }
        }

        /* Le header (BrixsaHeader) gère lui-même son espacement selon l'état
           haut/scroll — pas d'override ici. */

        /* Logo desktop +15% (≥768px uniquement) : middle 19vh→22vh, max 243→280px,
           maxWidth 77→89vw. Mobile (<768px) garde le style inline original. */
        @media (min-width: 768px) {
          .demo-hero-logo {
            height: clamp(121px, 22vh, 280px) !important;
            max-width: 89vw !important;
          }
        }
      `}</style>

      {/* 1️⃣ Background — vidéo multi-source (ou poster si reduced-motion / Save-Data) */}
      <div className="absolute inset-0">
        {showVideo ? (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={poster}
            aria-hidden
          >
            {/* Mobile (≤768px) — ~9.6 MB H.264 1080p paysage, servi depuis R2 */}
            <source media="(max-width: 768px)" src={videoMobile} type="video/mp4" />
            {/* Desktop — ~43 MB H.264 1920p CRF 12, servi depuis R2 (Cloudflare edge) */}
            <source src={videoDesktop} type="video/mp4" />
          </video>
        ) : (
          <Image
            src={poster}
            alt=""
            fill
            priority
            quality={90}
            sizes="100vw"
            className="object-cover"
            style={{ objectPosition: posterObjectPosition }}
          />
        )}
        {/* Overlay EXACT maquette : dégradé vertical léger + halo central marine
            (assombrit juste derrière le titre, laisse les côtés lumineux). */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(4,10,24,0.64) 0%, rgba(4,10,24,0.44) 24%, rgba(4,10,24,0.40) 48%, rgba(4,10,24,0.54) 70%, rgba(4,10,24,0.74) 100%), radial-gradient(ellipse 72% 60% at 50% 50%, rgba(4,10,24,0.46), rgba(4,10,24,0.14) 70%, rgba(4,10,24,0) 92%)',
          }}
        />
      </div>

      {/* 2️⃣ Stack vertical V9+A : logo complet BN transparent (top) +
             tagline Cinzel UPPERCASE tracked (middle) +
             bouton "Rejoindre le Club" style BN minimaliste (bottom). */}
      <div
        className="demo-hero-stack relative z-10 flex flex-col items-center text-center px-4 md:px-10"
        style={{ minHeight: '100vh' }}
      >
        {/* TOP — logo complet (sceau + wordmark intégrés, PNG transparent).
             Masqué si hideLogo : la navbar porte alors seule le logo, et on
             réserve la hauteur de la navbar en haut pour que le titre se centre
             ENTRE le bas de la navbar et le haut du bouton (pas dans tout le hero). */}
        {hideLogo ? (
          // Hauteur calée pour que le CENTRE du titre tombe pile au milieu
          // entre le bas de la navbar (~132px) et le haut du bouton : il faut
          // compenser la navbar + le décalage du bloc bouton + la date sous le
          // titre (~200px sur desktop). min() borne la valeur sur les grands
          // écrans et suit le viewport sur mobile.
          <div aria-hidden className="hero-top-spacer" />
        ) : (
          <div
            className="w-full flex justify-center"
            style={{ paddingTop: 'clamp(2px, 0.5vh, 8px)' }}
          >
            <Image
              src={logo}
              alt="Gala des Étoiles"
              width={2048}
              height={1365}
              priority
              sizes="(max-width: 768px) 65vw, 28vw"
              className="w-auto demo-hero-logo"
              style={{
                height: 'clamp(121px, 19vh, 243px)',
                maxWidth: '77vw',
                objectFit: 'contain',
                filter: 'drop-shadow(0 6px 24px rgba(0,0,0,0.5))',
              }}
            />
          </div>
        )}

        {/* MIDDLE — titre + (date / mention billetterie de l'événement) */}
        <div className="flex-1 flex flex-col items-center justify-center w-full">
          <h1
            className="text-white"
            style={{
              fontFamily: 'var(--font-display), Georgia, serif',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              // Grand titre façon maquette (Italiana caps) — ~32px mobile → ~90px desktop
              fontSize: 'clamp(1.5rem, 0.5rem + 4vw, 4.6rem)',
              maxWidth: '94vw',
              lineHeight: 1.02,
              textShadow: '0 2px 24px rgba(18,20,30,0.92), 0 0 60px rgba(18,20,30,0.6), 0 0 3px rgba(18,20,30,0.5)',
            }}
          >
            <span style={{ display: 'block' }}>{titre}</span>
          </h1>
          {montrerSousTitre && (
            <p style={{ fontFamily: 'var(--font-heading, inherit)', fontStyle: 'italic', color: 'white', fontSize: 'clamp(15px, 1rem + 0.55vw, 22px)', marginTop: 'clamp(12px, 1.8vh, 20px)', opacity: 0.94, letterSpacing: '0.01em', textShadow: '0 1px 16px rgba(18,20,30,0.7)' }}>{sousTitre}</p>
          )}
          {(mention || dateAffichee) && (
            <div style={{ marginTop: 'clamp(16px, 2.5vh, 26px)', textAlign: 'center', maxWidth: '94vw' }}>
              {mention && <div style={{ color: 'white', fontSize: 'clamp(10px, 0.6rem + 0.15vw, 12px)', letterSpacing: '0.26em', textTransform: 'uppercase', fontWeight: 600, opacity: 0.95, textShadow: '0 1px 14px rgba(18,20,30,0.6)' }}>{mention}</div>}
              {/* Logo du Grand Gala des Étoiles (l'événement) — Gala des Étoiles reste le titre principal au-dessus */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/media/brand/logo-grand-gala.png" alt="Grand Gala des Étoiles — Bal de l'Infini" style={{ display: 'block', margin: 'clamp(16px, 2.4vh, 26px) auto 0', height: 'auto', width: 'clamp(200px, 34vw, 340px)', filter: 'drop-shadow(0 2px 20px rgba(0,0,0,0.55))' }} />
              {dateAffichee && <div style={{ color: 'white', fontSize: 'clamp(15px, 0.9rem + 0.45vw, 22px)', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 600, opacity: 0.96, marginTop: 'clamp(18px, 2.4vh, 26px)', textShadow: '0 1px 16px rgba(18,20,30,0.7)' }}>{dateAffichee}</div>}
              <div style={{ color: 'white', fontSize: 'clamp(9px, 0.55rem + 0.15vw, 11px)', letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 500, opacity: 0.8, marginTop: '10px', textShadow: '0 1px 14px rgba(18,20,30,0.6)' }}>Fashion Week · Dîner de Gala · Bal de l&apos;Infini</div>
            </div>
          )}
        </div>

        {/* BOTTOM — trait blanc + bouton style BN minimaliste (masqué en hero titre-seul) */}
        {montrerCta && (
        <div
          className="flex flex-col items-center"
          style={{ paddingBottom: 'clamp(40px, 7vh, 88px)' }}
        >
          <div
            aria-hidden
            style={{
              width: 48,
              height: 1,
              background: 'rgba(255,255,255,0.6)',
              marginBottom: 'clamp(20px, 3vh, 32px)',
            }}
          />
          <a href={ctaHref} className="gde-btn gde-btn--on-dark">
            {ctaLabel}
          </a>
        </div>
        )}
      </div>
    </section>
  )
}
